/**
 * Document utility functions for CPF and alphanumeric CNPJ formatting/validation.
 *
 * CNPJ Alfanumérico (IN RFB 2.229/2024, effective July 2026):
 * - 14 characters: first 12 alphanumeric (0-9, A-Z), last 2 numeric (check digits)
 * - Formatted: AA.AAA.AAA/AAAA-DD
 * - Check digits: Modulo 11 with ASCII conversion (charCode - 48)
 */

/**
 * Strips formatting characters (dots, dashes, slashes) from a document string.
 * Preserves letters and digits.
 */
export function cleanDocument(formattedValue) {
  return formattedValue.replace(/[.\-/]/g, "");
}

/**
 * Detects whether raw characters represent a CPF or CNPJ.
 * - Contains letters → CNPJ
 * - More than 11 characters → CNPJ
 * - Otherwise → CPF
 */
export function detectDocumentType(rawChars) {
  if (/[A-Z]/.test(rawChars) || rawChars.length > 11) {
    return "cnpj";
  }
  return "cpf";
}

/**
 * Formats a document value (CPF or CNPJ) as the user types.
 * - Auto-detects type based on content
 * - Converts to uppercase, filters forbidden letters (I, O, Q, F)
 * - Applies CPF mask (000.000.000-00) or CNPJ mask (AA.AAA.AAA/AAAA-DD)
 */
export function formatDocument(inputValue) {
  // Strip formatting, uppercase, keep only allowed chars
  const stripped = inputValue.replace(/[.\-/]/g, "").toUpperCase();

  // Filter: keep digits and uppercase letters only
  let chars = "";
  for (const ch of stripped) {
    if (/[0-9A-Z]/.test(ch)) {
      chars += ch;
    }
  }

  const type = detectDocumentType(chars);

  if (type === "cpf") {
    // Limit to 11 digits
    const limited = chars.slice(0, 11);
    return formatCPFMask(limited);
  } else {
    // Limit to 14 characters
    const limited = chars.slice(0, 14);
    return formatCNPJMask(limited);
  }
}

function formatCPFMask(chars) {
  if (chars.length <= 3) return chars;
  if (chars.length <= 6) return `${chars.slice(0, 3)}.${chars.slice(3)}`;
  if (chars.length <= 9)
    return `${chars.slice(0, 3)}.${chars.slice(3, 6)}.${chars.slice(6)}`;
  return `${chars.slice(0, 3)}.${chars.slice(3, 6)}.${chars.slice(6, 9)}-${chars.slice(9)}`;
}

function formatCNPJMask(chars) {
  if (chars.length <= 2) return chars;
  if (chars.length <= 5) return `${chars.slice(0, 2)}.${chars.slice(2)}`;
  if (chars.length <= 8)
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5)}`;
  if (chars.length <= 12)
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8)}`;
  return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8, 12)}-${chars.slice(12)}`;
}

/**
 * Validates CNPJ check digits using Modulo 11 with ASCII conversion.
 * @param {string} chars - 14-character raw CNPJ (no formatting)
 * @returns {boolean}
 */
export function validateCNPJCheckDigits(chars) {
  if (chars.length !== 14) return false;

  // Last 2 must be digits
  if (!/^\d{2}$/.test(chars.slice(12))) return false;

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  // Convert each character to its numeric value via ASCII (charCode - 48)
  const values = [];
  for (let i = 0; i < 14; i++) {
    values.push(chars.charCodeAt(i) - 48);
  }

  // First check digit
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += values[i] * weights1[i];
  }
  let remainder = sum % 11;
  const dv1 = remainder < 2 ? 0 : 11 - remainder;
  if (dv1 !== parseInt(chars[12], 10)) return false;

  // Second check digit
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += values[i] * weights2[i];
  }
  remainder = sum % 11;
  const dv2 = remainder < 2 ? 0 : 11 - remainder;
  if (dv2 !== parseInt(chars[13], 10)) return false;

  return true;
}

/**
 * Validates a document (CPF or CNPJ).
 * @param {string} rawValue - Raw value (with or without formatting)
 * @returns {{ valid: boolean, type: string, error?: string }}
 */
export function validateDocument(rawValue) {
  const cleaned = cleanDocument(rawValue);
  const type = detectDocumentType(cleaned);

  if (type === "cpf") {
    if (cleaned.length !== 11) {
      return { valid: false, type: "cpf", error: "CPF deve ter 11 dígitos" };
    }
    if (!/^\d{11}$/.test(cleaned)) {
      return {
        valid: false,
        type: "cpf",
        error: "CPF deve conter apenas números",
      };
    }
    if (/^(\d)\1{10}$/.test(cleaned)) {
      return { valid: false, type: "cpf", error: "CPF inválido" };
    }
    return { valid: true, type: "cpf" };
  }

  // CNPJ
  if (cleaned.length !== 14) {
    return { valid: false, type: "cnpj", error: "CNPJ deve ter 14 caracteres" };
  }

  // Check last 2 are digits
  if (!/\d{2}$/.test(cleaned)) {
    return {
      valid: false,
      type: "cnpj",
      error: "Dígitos verificadores do CNPJ devem ser numéricos",
    };
  }

  // Check first 12 are alphanumeric (digits or uppercase letters)
  const body = cleaned.slice(0, 12);
  for (const ch of body) {
    if (!/[0-9A-Z]/.test(ch)) {
      return {
        valid: false,
        type: "cnpj",
        error: `Caractere '${ch}' não permitido no CNPJ`,
      };
    }
  }

  // Validate check digits
  if (!validateCNPJCheckDigits(cleaned)) {
    return {
      valid: false,
      type: "cnpj",
      error: "CNPJ inválido (dígito verificador incorreto)",
    };
  }

  return { valid: true, type: "cnpj" };
}
